/* ModelConfidenceChip — a plain-English pill showing how much the
 * weather models agree on the next few hours. Tappable → opens an
 * explainer sheet that tells users (in everyday language, no jargon)
 * what "model agreement" actually means.
 *
 * The chip mirrors the dominant agreement state across the next 6 h
 * of the forecast (we don't wave a yellow flag for one hour of slight
 * spread mid-week).
 */
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Question, WarningCircle, X, GlobeHemisphereWest } from "@phosphor-icons/react";

const STATES = {
  high:     { label: "Models agree",         tone: "#10b981", soft: "rgba(16,185,129,0.12)", Icon: CheckCircle },
  moderate: { label: "Models partly agree",  tone: "#f59e0b", soft: "rgba(245,158,11,0.14)", Icon: Question },
  low:      { label: "Forecast less certain",tone: "#d97706", soft: "rgba(217,119,6,0.14)",  Icon: WarningCircle },
  unknown:  { label: "Confidence loading",   tone: "#94a3b8", soft: "rgba(148,163,184,0.14)", Icon: Question },
};

function summarise(agreement, spreads) {
  if (!agreement || !agreement.length) return "unknown";
  const next6 = agreement.slice(0, 6).filter((a) => a && a !== "unknown");
  if (!next6.length) return "unknown";
  // Worst-case wins so we don't paint over uncertainty in any of the
  // critical near-term hours. (Avg would mask "disagreement at hour 4".)
  if (next6.includes("low")) return "low";
  if (next6.includes("moderate")) return "moderate";
  return "high";
}

export default function ModelConfidenceChip({ hourly, theme }) {
  const [open, setOpen] = useState(false);
  const state = useMemo(
    () => summarise(hourly?.model_agreement, hourly?.temperature_2m_band_spread),
    [hourly],
  );
  const meta = STATES[state] || STATES.unknown;
  const peakSpread = useMemo(() => {
    const arr = (hourly?.temperature_2m_band_spread || []).slice(0, 6).filter((v) => v != null);
    return arr.length ? Math.max(...arr) : null;
  }, [hourly]);

  if (state === "unknown") return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="model-confidence-chip"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-transform active:scale-95"
        style={{
          background: meta.soft,
          border: `1px solid ${meta.tone}40`,
          color: meta.tone,
        }}
      >
        <meta.Icon size={11} weight="fill" />
        <span className="text-[10px] font-bold uppercase tracking-wider">{meta.label}</span>
        {peakSpread != null && (
          <span
            className="text-[10px] font-medium tabular-nums opacity-80"
            style={{ color: meta.tone }}
          >
            ±{peakSpread.toFixed(1)}°
          </span>
        )}
      </button>

      <ExplainerSheet
        open={open}
        onClose={() => setOpen(false)}
        state={state}
        peakSpread={peakSpread}
        theme={theme}
      />
    </>
  );
}

function ExplainerSheet({ open, onClose, state, peakSpread, theme }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40"
            style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="confidence-sheet-scrim"
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl px-6 pt-3 pb-8 shadow-2xl"
            style={{
              background: theme.gradient,
              borderTop: `1px solid ${theme.surfaceBorder}`,
              color: theme.textPrimary,
              maxHeight: "min(80vh, 640px)",
              overflowY: "auto",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 280, damping: 32 }}
            data-testid="confidence-sheet"
          >
            {/* Drag handle */}
            <div className="mx-auto mb-3 h-1 w-10 rounded-full" style={{ background: theme.textMuted, opacity: 0.5 }} />

            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <GlobeHemisphereWest size={20} weight="duotone" style={{ color: theme.accent }} />
                <h2
                  className="font-display font-semibold tracking-tight"
                  style={{ fontSize: 19, color: theme.textPrimary }}
                >
                  How sure are we?
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: theme.chip, color: theme.textSecondary }}
              >
                <X size={12} weight="bold" />
              </button>
            </div>

            {/* Current state callout */}
            <div
              className="rounded-2xl px-4 py-3 mb-4"
              style={{
                background: STATES[state].soft,
                border: `1px solid ${STATES[state].tone}40`,
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                {(() => { const Ic = STATES[state].Icon; return <Ic size={16} weight="fill" style={{ color: STATES[state].tone }} />; })()}
                <span
                  className="font-display font-bold tracking-tight"
                  style={{ fontSize: 15, color: STATES[state].tone }}
                >
                  {STATES[state].label}
                </span>
              </div>
              <p className="text-[12.5px] leading-snug" style={{ color: theme.textSecondary }}>
                {state === "high" &&
                  "Three world-class models all land within 1°C of each other. You can plan around the temperatures we show."}
                {state === "moderate" &&
                  "Models are leaning the same way but with up to a degree or two of wobble. The headline is reliable; expect small surprises."}
                {state === "low" &&
                  "Models are spread out — the atmosphere is in transition. Take the forecast as a centre-point and prepare for either side."}
                {peakSpread != null && (
                  <span className="block mt-1 opacity-80">
                    Biggest gap in the next 6 h:{" "}
                    <span className="font-semibold tabular-nums" style={{ color: STATES[state].tone }}>
                      ±{peakSpread.toFixed(1)}°C
                    </span>
                  </span>
                )}
              </p>
            </div>

            {/* What is "model agreement"? */}
            <h3
              className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2"
              style={{ color: theme.textMuted }}
            >
              What does this mean?
            </h3>
            <p className="text-[13px] leading-relaxed mb-4" style={{ color: theme.textSecondary }}>
              Weather forecasts come from <strong style={{ color: theme.textPrimary }}>giant computer
              simulations</strong> running on supercomputers. Different countries (Europe, USA, Germany)
              run different models — and they don&apos;t always agree.
            </p>

            <ul className="space-y-3 mb-4">
              <Bullet
                tone="#10b981"
                Icon={CheckCircle}
                title="When models agree"
                body="The atmosphere is steady. The forecast is reliable — the actual temperature will land very close to what we show."
              />
              <Bullet
                tone="#f59e0b"
                Icon={Question}
                title="When models partly agree"
                body="Models lean the same way but with small wobbles. The general shape of the day is right; details may shift by a degree or two."
              />
              <Bullet
                tone="#ef4444"
                Icon={WarningCircle}
                title="When models disagree"
                body="A storm front, monsoon trough, or transition is brewing. Reality could swing either way within the range. Plan for the worst case."
              />
            </ul>

            <h3
              className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2"
              style={{ color: theme.textMuted }}
            >
              How we use it
            </h3>
            <p className="text-[13px] leading-relaxed" style={{ color: theme.textSecondary }}>
              We blend the three best global models (ECMWF, GFS and Open-Meteo&apos;s seamless mix) and
              show you both the <strong style={{ color: theme.textPrimary }}>most likely value</strong>{" "}
              and the <strong style={{ color: theme.textPrimary }}>realistic range</strong>. No fake
              single-decimal precision when the atmosphere is unsure.
            </p>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Bullet({ tone, Icon, title, body }) {
  return (
    <li className="flex gap-2.5">
      <Icon size={16} weight="fill" style={{ color: tone, flexShrink: 0, marginTop: 1.5 }} />
      <div>
        <p className="text-[13px] font-semibold leading-tight" style={{ color: tone }}>
          {title}
        </p>
        <p className="text-[12.5px] leading-snug mt-0.5 opacity-90">{body}</p>
      </div>
    </li>
  );
}
